import React, { useState, useEffect, useCallback, memo } from "react";
import Pagination from "../pagination";
import ReferralTable from "./ReferralTable";
import { useToastStore } from "store/toastStore";
import {
  getAllReferralCreditConfig,
  updateReferralCreditConfig,
  ReferralCreditConfigAPI,
  ReferralCreditConfigRequest,
} from "apis/adminServices/referralServices";

const ROWS_PER_PAGE = 10;

const ReferralManagement: React.FC = () => {
  const { showToast } = useToastStore();

  const [configurations, setConfigurations] = useState<ReferralCreditConfigAPI[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllReferralCreditConfig();
      setConfigurations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch Error:", err);
      setConfigurations([]);
      showToast("Failed to fetch referral configurations", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const paginated = configurations.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const handleUpdateConfig = useCallback(
    async (id: number, payload: ReferralCreditConfigRequest) => {
      try {
        const response = await updateReferralCreditConfig(id, payload);
        showToast(response.message || "Referral configuration updated successfully", "success");
        await fetchConfigurations();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update referral configuration";
        showToast(errorMessage, "error");
        throw error;
      }
    },
    [fetchConfigurations, showToast]
  );

  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
        <p className="text-grease-100 text-sm font-semibold">Referral Management</p>
      </div>

      <ReferralTable
        configurations={paginated}
        loading={loading}
        onUpdate={handleUpdateConfig}
      />

      <Pagination
        currentPage={currentPage}
        totalItems={configurations.length}
        onPageChange={setCurrentPage}
      />
    </section>
  );
};

export default memo(ReferralManagement);
